import { useBottomSheet } from '@croquiscom-pvt/zds';

import { DEFAULT_MAX_COUNT, DEFAULT_MIN_COUNT, OPTION_TYPE } from '../../constant/product-option';
import { useProductOptionContext } from '../../context/ProductOptionProvider';
import { getItemCardShippingData, getMinCountByOrderRule } from '../../core/item-card.core';
import { ItemCardType, ProductOptionConfig } from '../../type/common.type';
import {
  checkZigzinOrVendorFlexDeliveryType,
  checkZigzinOrVendorFlexFulfillmentType,
} from '../../util/checkers/zigzin-checker';
import { showZDSToastInOption } from '../../util/toast/zds-toast-in-option';
import {
  getLimitOverMaxMessage,
  getMinimumOrderQuantityToastMessage,
} from '../../util/transformers/message-transformer';
import { productMetadataBuilder } from '../../util/transformers/product-metadata-transformer';

interface UseItemCardProps {
  config: ProductOptionConfig;
}

export const useItemCards = ({ config }: UseItemCardProps) => {
  const { open: openBottomSheet, close: closeBottomSheet } = useBottomSheet('confirm');
  const { state, actions } = useProductOptionContext();
  const product_metadata = productMetadataBuilder(state.option.info, config);
  const catalog_product = state.option.info?.catalog_product;
  const product_option_list = catalog_product?.product_option_list || [];
  const custom_input_option_list = catalog_product?.custom_input_option_list || [];
  const hasNoOptions = product_option_list.length === 0 && custom_input_option_list.length === 0;

  // 아이템 카드 제거
  const handleRemoveItemCard = (item: ItemCardType) => {
    const is_last_basic_item =
      state.item_cards.list.filter((o) => o.option_type !== OPTION_TYPE.ADDITIONAL).length === 1;

    const has_additional_item = state.item_cards.list.find((o) => o.option_type === OPTION_TYPE.ADDITIONAL);
    const display_confirm_bottom_sheet =
      is_last_basic_item && has_additional_item && item.option_type !== OPTION_TYPE.ADDITIONAL;

    // 기본 옵션이 1개 + 추가 옵션 선택된 경우 기본 옵션 삭제 시 확인 바텀씻 노출
    if (display_confirm_bottom_sheet) {
      openBottomSheet({
        title: '',
        description: '기본상품을 모두 삭제하는 경우\n추가상품도 모두 삭제됩니다.\n선택하신 상품을 삭제하시겠습니까?',
        actionButtonText: '삭제',
        subActionButtonText: '취소',
        onClickActionButton: () => {
          actions.resetOptionView({
            enabledShowOnlyZigzinOption: config?.enabledShowOnlyZigzinOption ?? false,
          });
          closeBottomSheet();
        },
        onClickSubActionButton: closeBottomSheet,
      });
    } else {
      actions.removeItemCard({ item });
    }
  };

  // 아이템 카드 수량 증가
  const handleItemCardIncrease = (item: ItemCardType) => {
    if (!isIncreasedDisabledButton(item)) {
      actions.incrementItemCount(item);
      return;
    }

    const limit_over_max_message = getLimitOverMaxMessage(
      product_metadata.has_first_order_discount,
      product_metadata.has_store_deal_banner,
    );
    if (limit_over_max_message) {
      showZDSToastInOption(limit_over_max_message);
    }
  };

  // 아이템 카드 수량 감소
  const handleItemCardDecrease = (item: ItemCardType) => {
    if (isOrderQuantityProductDecreaseBlocked(item.count)) {
      return;
    }

    const min_count = getMinCountByOrderRule(
      item,
      state.item_cards.total_count,
      product_metadata.min_order_quantity,
      product_metadata.is_order_quantity_product_type,
    );

    const under_min_message = getMinimumOrderQuantityToastMessage(
      product_metadata.minimum_order_quantity_type,
      product_metadata.min_order_quantity,
    );

    if (item.count > min_count) {
      actions.decrementItemCount(item);
    } else if (under_min_message) {
      showZDSToastInOption(under_min_message);
    }
  };

  // 수량 증가 버튼 비활성화 여부
  const isIncreasedDisabledButton = (item: ItemCardType) => {
    const is_zigzin_fulfillment = checkZigzinOrVendorFlexFulfillmentType(
      state.option.info?.catalog_product?.fulfillment_type ?? null,
    );
    // 매칭 옵션의 경우는 제외하기 위해 같은 상품 타입인지 확인
    const same_product_type_option = is_zigzin_fulfillment === checkZigzinOrVendorFlexDeliveryType(item.delivery_type);
    const max_count = product_metadata.has_first_order_discount && same_product_type_option ? 1 : DEFAULT_MAX_COUNT;
    return product_metadata.disabled_counter_button || max_count <= item.count;
  };

  // 수량 감소 버튼 비활성화 여부
  const isDisalbedDecreaseButton = (item: ItemCardType) => {
    const min_count = getMinCountByOrderRule(
      item,
      state.item_cards.total_count,
      product_metadata.min_order_quantity,
      product_metadata.is_order_quantity_product_type,
    );
    return (
      product_metadata.disabled_counter_button ||
      isOrderQuantityProductDecreaseBlocked(item.count) ||
      min_count >= item.count
    );
  };

  /**
   * ex) 최소 주문 갯수 3개
   * A 옵션 3개, B 옵션 1개인 상태에서 B 옵션 감소 액션인 경우 토스트 노출 x 및 감소 스템프 비활성화
   */
  const isOrderQuantityProductDecreaseBlocked = (count: number) => {
    return (
      product_metadata.is_order_quantity_product_type &&
      state.item_cards.total_count > product_metadata.min_order_quantity &&
      count === DEFAULT_MIN_COUNT
    );
  };

  return {
    display: !config.hidden_flags?.item_card && state.ui.is_item_card_mode_active,
    itemCardList: state.item_cards.list,
    bogoBadgeUrl: state.option.info?.catalog_product?.promotion_info?.bogo_info?.badge?.image_url,
    displayShipping: !(config.hidden_flags?.item_card_shipping ?? false),
    displayDeliveryDate: !(config.hidden_flags?.item_card_delivery_date ?? false),
    displayProductInfo: !(config.hidden_flags?.item_card_product_info ?? false),
    displayCounter: !(config.hidden_flags?.item_card_counter ?? false),
    displayPrice: !(config.hidden_flags?.item_card_price ?? false),
    displayRemove: !hasNoOptions && !(config.hidden_flags?.item_card_remove ?? false),
    actions: {
      getItemCardShippingData,
      isDisalbedDecreaseButton,
      isIncreasedDisabledButton,
      handleItemCardIncrease,
      handleItemCardDecrease,
      handleRemoveItemCard,
    },
  };
};
